/**
 * Ansichtsmodell des Skill- und Talent-Menues
 * (Masterprompt Abschnitt 46, VISUAL_REFERENCE Abschnitte 46a und 46c).
 *
 * Hier wird die Levelstrecke gebaut, die links im Menue steht. Sie ist reine
 * Rechnung aus dem Snapshot - deshalb steht sie hier und nicht im Bauteil.
 *
 * Aufbau der Strecke (Produktentscheidung vom 27.09.2026):
 *
 *   Lv 1   Startskills: die drei Grundskills, dazu das noch verschlossene
 *          Ultimate
 *   Lv 2   ein Skillpunkt
 *   Lv 4   ein Skillpunkt
 *   Lv 5   das Ultimate - oeffnet sich von selbst, hier gibt es nichts zu
 *          entscheiden
 *   Lv 6   ein Skillpunkt
 *   Lv 8   Talent, endgueltig
 *   Lv 10  Capstone, endgueltig
 *
 * Die Strecke endet bei Level 10. Ab Level 11 wachsen nur noch Leben, Mana und
 * Schaden - dafuer braucht es keine Stufe auf der Strecke.
 *
 * Eine Sache laesst der Serverzustand nicht zu: Er sagt, WIE WEIT jeder Skill
 * ausgebaut ist, aber nicht, WELCHER Punkt dafuer ausgegeben wurde. Die schon
 * vergebenen Punkte bekommen deshalb der Reihe nach das, was man besitzt -
 * nach Slot und Stufe sortiert. Die Menge stimmt immer, die Zuordnung ist eine
 * Darstellungsfrage. Wer zwei Punkte ausgegeben hat, sieht zwei abgehakte
 * Stufen; welche davon auf Level 2 und welche auf Level 4 faellt, ist im Spiel
 * ohne Bedeutung.
 */

import {
  CAPSTONE_LEVEL,
  MAX_SKILL_UPGRADE_STEP,
  SKILL_POINT_LEVELS,
  TALENT_LEVEL,
  ULTIMATE_UNLOCK_LEVEL,
  getCapstones,
  getSkillUpgrades,
  getSkillsForHero,
  getTalents,
  type SkillUpgradeDefinition,
} from '@dueline/game-data';

/** Was auf einer Stufe der Strecke passiert. */
export type LevelRowKind = 'start' | 'point' | 'ultimate' | 'talent' | 'capstone';

/** Zustand einer Wahlmoeglichkeit. */
export type ChoiceState = 'owned' | 'available' | 'locked';

export interface SkillChoice {
  /** ID des Upgrades, Talents oder Capstones. Bei Startskills die Skill-ID. */
  id: string;
  name: string;
  description: string;
  kind: LevelRowKind;
  state: ChoiceState;
  /** Nur bei Skill-Upgrades: welcher Grundskill und welche Stufe. */
  slot?: number;
  step?: number;
}

export interface LevelRow {
  level: number;
  kind: LevelRowKind;
  /** Hat der Hero diese Stufe schon erreicht? */
  reached: boolean;
  /**
   * Bei einem Skillpunkt: Ist er noch frei? Bei Talent und Capstone: Steht die
   * Wahl noch offen?
   */
  open: boolean;
  choices: SkillChoice[];
}

/** Was das Menue aus dem Match wissen muss. */
export interface SkillTreeInput {
  heroId: string;
  level: number;
  /** Freie Skillpunkte laut Server. */
  skillPoints: number;
  /** Ausbaustufe je Grundskill-Slot. */
  skillUpgrades: Record<number, number>;
  talentPending: boolean;
  capstonePending: boolean;
  talentId: string;
  capstoneId: string;
}

/** Die Grundskills eines Heroes, ohne das Ultimate. */
function grundskills(heroId: string) {
  return getSkillsForHero(heroId).filter((skill) => skill.slot !== 4);
}

/** Das Ultimate eines Heroes. */
function ultimate(heroId: string) {
  return getSkillsForHero(heroId).find((skill) => skill.slot === 4);
}

/**
 * Alle bereits gekauften Ausbaustufen, nach Slot und Stufe sortiert.
 *
 * Diese Liste fuellt die schon vergebenen Punkte auf der Strecke.
 */
export function ownedUpgrades(input: SkillTreeInput): SkillUpgradeDefinition[] {
  const gekauft: SkillUpgradeDefinition[] = [];

  for (const skill of grundskills(input.heroId)) {
    const stufe = input.skillUpgrades[skill.slot] ?? 0;
    for (const upgrade of getSkillUpgrades(input.heroId, skill.slot)) {
      if (upgrade.step <= stufe) gekauft.push(upgrade);
    }
  }

  return gekauft;
}

/**
 * Die Ausbaustufen, die ein freier Punkt gerade kaufen koennte: je Grundskill
 * die naechste offene Stufe, hoechstens drei Karten.
 */
export function openUpgrades(input: SkillTreeInput): SkillUpgradeDefinition[] {
  const offen: SkillUpgradeDefinition[] = [];

  for (const skill of grundskills(input.heroId)) {
    const stufe = input.skillUpgrades[skill.slot] ?? 0;
    if (stufe >= MAX_SKILL_UPGRADE_STEP) continue;

    const naechste = getSkillUpgrades(input.heroId, skill.slot).find(
      (upgrade) => upgrade.step === stufe + 1,
    );
    if (naechste) offen.push(naechste);
  }

  return offen;
}

/** Baut die ganze Levelstrecke. */
export function buildLevelPlan(input: SkillTreeInput): LevelRow[] {
  const zeilen: LevelRow[] = [];

  /* --- Level 1: die Startskills ---------------------------------------- */
  const start: SkillChoice[] = grundskills(input.heroId).map((skill) => ({
    id: skill.id,
    name: skill.name,
    description: skill.description,
    kind: 'start',
    state: 'owned',
    slot: skill.slot,
  }));

  const ulti = ultimate(input.heroId);
  if (ulti) {
    start.push({
      id: ulti.id,
      name: ulti.name,
      description: ulti.description,
      kind: 'ultimate',
      state: input.level >= ULTIMATE_UNLOCK_LEVEL ? 'owned' : 'locked',
      slot: ulti.slot,
    });
  }

  zeilen.push({ level: 1, kind: 'start', reached: true, open: false, choices: start });

  /* --- Die Skillpunkte auf 2, 4 und 6 ----------------------------------- */
  const besitz = ownedUpgrades(input);
  const offen = openUpgrades(input);
  const vergeben = besitz.length;

  const punktZeilen: LevelRow[] = SKILL_POINT_LEVELS.map((level, index) => {
    const erreicht = input.level >= level;

    // Die ersten "vergeben" Punkte sind ausgegeben - sie zeigen, was sie
    // gekauft haben.
    if (index < vergeben) {
      const gekauft = besitz[index];
      return {
        level,
        kind: 'point',
        reached: erreicht,
        open: false,
        choices: gekauft
          ? [
              {
                id: gekauft.id,
                name: gekauft.name,
                description: gekauft.description,
                kind: 'point',
                state: 'owned',
                slot: gekauft.slot,
                step: gekauft.step,
              },
            ]
          : [],
      };
    }

    // Der erste noch freie Punkt zeigt die Auswahl. Weitere freie Punkte
    // zeigen keine zweite Kopie derselben Karten - das waere nur Laerm.
    const ersterFreier = index === vergeben;

    return {
      level,
      kind: 'point',
      reached: erreicht,
      open: erreicht && input.skillPoints > 0,
      choices:
        erreicht && ersterFreier
          ? offen.map((upgrade) => ({
              id: upgrade.id,
              name: upgrade.name,
              description: upgrade.description,
              kind: 'point' as const,
              state: input.skillPoints > 0 ? ('available' as const) : ('locked' as const),
              slot: upgrade.slot,
              step: upgrade.step,
            }))
          : [],
    };
  });

  /* --- Level 5: das Ultimate, ohne Zutun -------------------------------- */
  const ultiZeile: LevelRow = {
    level: ULTIMATE_UNLOCK_LEVEL,
    kind: 'ultimate',
    reached: input.level >= ULTIMATE_UNLOCK_LEVEL,
    open: false,
    choices: ulti
      ? [
          {
            id: ulti.id,
            name: ulti.name,
            description: ulti.description,
            kind: 'ultimate',
            state: input.level >= ULTIMATE_UNLOCK_LEVEL ? 'owned' : 'locked',
            slot: ulti.slot,
          },
        ]
      : [],
  };

  /* --- Talent und Capstone ---------------------------------------------- */
  function wahlZeile(
    level: number,
    kind: 'talent' | 'capstone',
    moeglichkeiten: readonly { id: string; name: string; description: string }[],
    gewaehlt: string,
    offenJetzt: boolean,
  ): LevelRow {
    return {
      level,
      kind,
      reached: input.level >= level,
      open: offenJetzt && !gewaehlt,
      choices: moeglichkeiten.map((moeglichkeit) => ({
        id: moeglichkeit.id,
        name: moeglichkeit.name,
        description: moeglichkeit.description,
        kind,
        state: gewaehlt
          ? moeglichkeit.id === gewaehlt
            ? 'owned'
            : 'locked'
          : offenJetzt
            ? 'available'
            : 'locked',
      })),
    };
  }

  // Alle Zeilen in Levelreihenfolge - das Ultimate liegt zwischen den
  // Skillpunkten von Level 4 und Level 6.
  zeilen.push(
    ...[
      ...punktZeilen,
      ultiZeile,
      wahlZeile(
        TALENT_LEVEL,
        'talent',
        getTalents(input.heroId),
        input.talentId,
        input.talentPending,
      ),
      wahlZeile(
        CAPSTONE_LEVEL,
        'capstone',
        getCapstones(input.heroId),
        input.capstoneId,
        input.capstonePending,
      ),
    ].sort((a, b) => a.level - b.level),
  );

  return zeilen;
}

/** Warum eine Wahl gerade nicht geht - oder null, wenn sie geht. */
export type ChoiceBlocker = 'level_too_low' | 'no_skill_point' | 'already_chosen' | 'nothing_to_do';

export function getChoiceBlocker(input: SkillTreeInput, choice: SkillChoice): ChoiceBlocker | null {
  if (choice.kind === 'start' || choice.kind === 'ultimate') return 'nothing_to_do';

  if (choice.kind === 'point') {
    if (choice.state === 'owned') return 'already_chosen';
    if (input.skillPoints <= 0) return 'no_skill_point';
    return null;
  }

  const level = choice.kind === 'talent' ? TALENT_LEVEL : CAPSTONE_LEVEL;
  const gewaehlt = choice.kind === 'talent' ? input.talentId : input.capstoneId;
  const offen = choice.kind === 'talent' ? input.talentPending : input.capstonePending;

  if (gewaehlt) return 'already_chosen';
  if (input.level < level || !offen) return 'level_too_low';
  return null;
}
